import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { providerPaymentsService, TransferPaymentRow } from '../services/providerPaymentsService';
import { UserRole } from '../types';
import { format } from 'date-fns';
import { RootState } from '../store/store';

export const ProviderPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [transfers, setTransfers] = useState<TransferPaymentRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransfers();
  }, []);

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const data = await providerPaymentsService.getMyTransfers();
      setTransfers(data);
    } catch {
      toast.error(t('transfers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = transfers.reduce((sum, t) => sum + t.providerPrice, 0);
  const totalPaid = transfers.reduce((sum, t) => sum + t.totalPaid, 0);
  const totalUnpaid = transfers.reduce((sum, t) => sum + t.remaining, 0);

  // Only provider manager should see this page
  if (user?.role !== UserRole.PROVIDER_MANAGER) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payments.accessDenied')}</h2>
          <p className="text-gray-600">{t('payments.providersOnly')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-0">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('payments.myPayments')}</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">{t('payments.myPaymentsSubtitle')}</p>
      </div>

      {/* Payment Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-blue-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-blue-600">{t('payments.totalReceivables')}</p>
              <p className="text-xl sm:text-3xl font-bold text-blue-900">{totalAmount.toFixed(2)} TL</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-green-600">{t('payments.paid')}</p>
              <p className="text-xl sm:text-3xl font-bold text-green-900">{totalPaid.toFixed(2)} TL</p>
              <p className="text-xs text-green-700">{transfers.filter(t => t.isPaid).length} {t('payments.transfers')}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-orange-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-orange-600">{t('payments.pending')}</p>
              <p className="text-xl sm:text-3xl font-bold text-orange-900">{totalUnpaid.toFixed(2)} TL</p>
              <p className="text-xs text-orange-700">{transfers.filter(t => !t.isPaid).length} {t('payments.transfers')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('payments.paymentHistory')}</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">{t('payments.loading')}</div>
        ) : transfers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>{t('payments.noTransfersFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {transfers.map((transfer) => (
                <div key={transfer.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium text-gray-900">
                      {format(new Date(transfer.transferDate), 'dd.MM.yyyy')} {transfer.transferTime?.substring(0, 5)}
                    </p>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${transfer.isPaid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                      {transfer.isPaid ? `✓ Ödendi` : 'Bekliyor'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 mb-3">
                    {transfer.pickupLocation} <span className="text-gray-400">→</span> {transfer.dropoffLocation}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-gray-500">Toplam</p>
                      <p className="font-bold">{transfer.providerPrice.toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 rounded p-2">
                      <p className="text-green-600">Ödenen</p>
                      <p className="font-bold text-green-700">{transfer.totalPaid.toFixed(2)}</p>
                    </div>
                    <div className={`rounded p-2 ${transfer.remaining > 0 ? 'bg-orange-50' : 'bg-gray-50'}`}>
                      <p className={transfer.remaining > 0 ? 'text-orange-600' : 'text-gray-500'}>Kalan</p>
                      <p className={`font-bold ${transfer.remaining > 0 ? 'text-orange-700' : 'text-gray-500'}`}>{transfer.remaining.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Güzergah</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Toplam</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ödenen</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Kalan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{format(new Date(transfer.transferDate), 'dd.MM.yyyy')}</div>
                        <div className="text-gray-500">{transfer.transferTime?.substring(0, 5)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="text-gray-900">{transfer.pickupLocation}</div>
                        <div className="text-gray-500 text-xs">→ {transfer.dropoffLocation}</div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                        {transfer.providerPrice.toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                        {transfer.totalPaid.toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-semibold">
                        <span className={transfer.remaining > 0 ? 'text-orange-600' : 'text-gray-400'}>
                          {transfer.remaining.toFixed(2)} TL
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${transfer.isPaid ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                          {transfer.isPaid ? '✓ Ödendi' : 'Bekliyor'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

